/**
 * CloudImportModal - 云端导入向导 Modal
 *
 * 按照 3.0-phase-2-import-plan.md Section 5.1 设计：
 *
 * Step 1: 选择导入范围
 * - 标签组列表（checkbox，勾选=包含所有子标签）
 * - 未分组标签（checkbox）
 *
 * Step 2: 确认导入配置
 * - 导入统计预览
 * - 更新式上传选项（默认关闭=跳过已存在）
 * - 上传模式（自动选择，可调整）
 *
 * Step 3: 执行导入
 * - 整体进度条
 * - 当前阶段
 * - 暂停/取消按钮
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Steps, Button, Spin, Checkbox, Radio, Space, Alert, Progress } from 'antd';
import {
  CloudUploadOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  SettingOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { TagGroupSelector } from './TagGroupSelector';
import { useCloudImport } from '../hooks/useCloudImport';
import type {
  CloudImportConflictStrategy,
  CloudImportUploadMode,
  CloudImportPreviewStats,
} from '../types';

// 同步上传阈值（与 useCloudImport 保持一致）
const SYNC_THRESHOLD = 15;

export interface CloudImportModalProps {
  /** 是否显示 */
  visible: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 导入完成回调 */
  onComplete?: () => void;
  /** IPC 调用函数（Electron 专用） */
  ipcInvoke: <T>(channel: string, ...args: any[]) => Promise<T>;
}

export const CloudImportModal: React.FC<CloudImportModalProps> = ({
  visible,
  onClose,
  onComplete,
  ipcInvoke,
}) => {
  // 当前步骤
  const [currentStep, setCurrentStep] = useState(0);

  // 选中的标签组和标签
  const [selectedTagGroupIds, setSelectedTagGroupIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [includeUncategorized, setIncludeUncategorized] = useState(false);

  // 导入配置
  const [conflictStrategy, setConflictStrategy] = useState<CloudImportConflictStrategy>('SKIP');
  const [uploadMode, setUploadMode] = useState<CloudImportUploadMode>('SYNC');

  // 预览数据
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{
    tagGroups: any[];
    tags: any[];
    bloggers: any[];
    posts: any[];
  } | null>(null);

  // 使用云端导入 Hook
  const {
    loadingLocalData,
    localStructure,
    currentTask,
    currentProgress,
    previewStats,
    isImporting,
    error,
    fetchLocalStructure,
    fetchPreview,
    startImport,
    pauseImport,
    resumeImport,
    cancelImport,
    clearTask,
    suggestedUploadMode,
  } = useCloudImport({
    isElectron: true,
    ipcInvoke,
    onComplete: () => {
      onComplete?.();
    },
  });

  // 打开时加载本地数据结构
  useEffect(() => {
    if (visible && !localStructure) {
      fetchLocalStructure();
    }
  }, [visible, localStructure, fetchLocalStructure]);

  // 关闭时重置状态
  useEffect(() => {
    if (!visible) {
      setCurrentStep(0);
      setSelectedTagGroupIds([]);
      setSelectedTagIds([]);
      setIncludeUncategorized(false);
      setConflictStrategy('SKIP');
      setPreviewData(null);
    }
  }, [visible]);

  // 更新上传模式建议
  useEffect(() => {
    setUploadMode(suggestedUploadMode);
  }, [suggestedUploadMode]);

  // 处理选择变更
  const handleSelectionChange = useCallback(
    (selection: { selectedTagGroupIds: string[]; selectedTagIds: string[]; includeUncategorized?: boolean }) => {
      setSelectedTagGroupIds(selection.selectedTagGroupIds);
      setSelectedTagIds(selection.selectedTagIds);
      setIncludeUncategorized(selection.includeUncategorized ?? false);
    },
    []
  );

  // 下一步
  const handleNext = async () => {
    if (currentStep === 0) {
      // Step 1 -> Step 2: 获取预览数据
      setPreviewLoading(true);
      const data = await fetchPreview(selectedTagGroupIds, selectedTagIds, includeUncategorized);
      setPreviewData(data);
      setPreviewLoading(false);
      if (data) {
        setCurrentStep(1);
      }
    } else if (currentStep === 1) {
      // Step 2 -> Step 3: 开始导入
      setCurrentStep(2);
      await startImport({
        selectedTagGroupIds,
        selectedTagIds,
        includeUncategorized,
        conflictStrategy,
        uploadMode,
      });
    }
  };

  // 上一步
  const handlePrev = () => {
    if (currentStep > 0 && !isImporting) {
      setCurrentStep(currentStep - 1);
    }
  };

  // 关闭 Modal
  const handleClose = () => {
    if (isImporting) {
      Modal.confirm({
        title: '确认关闭',
        content: '导入正在进行中，关闭后导入将在后台继续。确定关闭吗？',
        okText: '关闭',
        cancelText: '取消',
        onOk: () => {
          onClose();
        },
      });
    } else {
      clearTask();
      onClose();
    }
  };

  // 计算是否可以下一步
  const canNext = (() => {
    if (currentStep === 0) {
      return selectedTagGroupIds.length > 0 || selectedTagIds.length > 0 || includeUncategorized;
    }
    if (currentStep === 1) {
      return previewStats && (previewStats.totalPosts > 0 || previewStats.totalBloggers > 0);
    }
    return false;
  })();

  // 渲染步骤内容
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      default:
        return null;
    }
  };

  // Step 1: 选择导入范围
  const renderStep1 = () => {
    if (loadingLocalData) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: 'var(--ant-color-text-secondary)' }}>
            正在加载本地数据...
          </div>
        </div>
      );
    }

    return (
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        <TagGroupSelector
          tagGroups={localStructure?.tagGroups || []}
          ungroupedTags={localStructure?.ungroupedTags || []}
          uncategorizedPostCount={localStructure?.uncategorizedPostCount || 0}
          selectedTagGroupIds={selectedTagGroupIds}
          selectedTagIds={selectedTagIds}
          includeUncategorized={includeUncategorized}
          onSelectionChange={handleSelectionChange}
          loading={loadingLocalData}
        />
      </div>
    );
  };

  // Step 2: 确认导入配置
  const renderStep2 = () => {
    if (previewLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: 'var(--ant-color-text-secondary)' }}>
            正在分析数据...
          </div>
        </div>
      );
    }

    const stats = previewStats;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* 导入统计预览 */}
        <div>
          <h4 style={{ marginBottom: 12, color: 'var(--ant-color-text)' }}>导入统计预览</h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
              padding: 16,
              backgroundColor: 'var(--ant-color-fill-quaternary)',
              borderRadius: 8,
            }}
          >
            <StatItem label="标签组" value={stats?.totalTagGroups || 0} />
            <StatItem label="标签" value={stats?.totalTags || 0} />
            <StatItem label="博主" value={stats?.totalBloggers || 0} />
            <StatItem label="帖子" value={stats?.totalPosts || 0} />
            <StatItem label="图片" value={stats?.totalImages || 0} />
            <StatItem label="视频" value={stats?.totalVideos || 0} />
          </div>
        </div>

        {/* 冲突处理选项 */}
        <div>
          <h4 style={{ marginBottom: 12, color: 'var(--ant-color-text)' }}>冲突处理</h4>
          <Checkbox
            checked={conflictStrategy === 'OVERWRITE'}
            onChange={(e) => setConflictStrategy(e.target.checked ? 'OVERWRITE' : 'SKIP')}
          >
            <span style={{ color: 'var(--ant-color-text)' }}>
              更新式上传（覆盖云端已存在的同名数据）
            </span>
          </Checkbox>
          <div style={{ marginTop: 4, marginLeft: 24, fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {conflictStrategy === 'SKIP'
              ? '默认：跳过云端已存在的数据'
              : '将覆盖云端同名的标签、博主和帖子'}
          </div>
        </div>

        {/* 上传模式 */}
        <div>
          <h4 style={{ marginBottom: 12, color: 'var(--ant-color-text)' }}>上传模式</h4>
          <Radio.Group
            value={uploadMode}
            onChange={(e) => setUploadMode(e.target.value)}
          >
            <Space direction="vertical">
              <Radio value="SYNC">
                <span style={{ color: 'var(--ant-color-text)' }}>同步上传</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
                  （推荐帖子 ≤{SYNC_THRESHOLD} 时使用）
                </span>
              </Radio>
              <Radio value="ASYNC">
                <span style={{ color: 'var(--ant-color-text)' }}>异步上传</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
                  （帖子较多时推荐，媒体后台上传）
                </span>
              </Radio>
            </Space>
          </Radio.Group>
          {stats && stats.totalPosts > SYNC_THRESHOLD && uploadMode === 'SYNC' && (
            <Alert
              type="warning"
              message={`当前选中 ${stats.totalPosts} 篇帖子，建议使用异步上传`}
              style={{ marginTop: 12 }}
              showIcon
            />
          )}
        </div>
      </div>
    );
  };

  // Step 3: 执行导入
  const renderStep3 = () => {
    const task = currentTask;
    const progress = currentProgress;

    if (!task) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: 'var(--ant-color-text-secondary)' }}>
            正在准备导入...
          </div>
        </div>
      );
    }

    const percentage = progress?.percentage ?? 0;
    const currentPhase = progress?.currentPhase ?? '准备中...';
    const isCompleted = task.status === 'COMPLETED';
    const isFailed = task.status === 'FAILED';
    const isCancelled = task.status === 'CANCELLED';
    const isPaused = task.status === 'PAUSED';
    const isFinished = isCompleted || isFailed || isCancelled;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* 状态图标 */}
        <div style={{ textAlign: 'center' }}>
          {isCompleted ? (
            <CheckCircleOutlined style={{ fontSize: 48, color: 'var(--ant-color-success)' }} />
          ) : isFailed ? (
            <CloudUploadOutlined style={{ fontSize: 48, color: 'var(--ant-color-error)' }} />
          ) : (
            <LoadingOutlined style={{ fontSize: 48, color: 'var(--ant-color-primary)' }} spin={!isPaused} />
          )}
        </div>

        {/* 当前阶段 */}
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ margin: 0, color: 'var(--ant-color-text)' }}>{currentPhase}</h3>
          <div style={{ marginTop: 8, fontSize: 24, fontWeight: 600, color: 'var(--ant-color-primary)' }}>
            {percentage}%
          </div>
        </div>

        {/* 进度条 */}
        <Progress
          percent={percentage}
          status={isCompleted ? 'success' : isFailed ? 'exception' : isPaused ? 'normal' : 'active'}
          strokeColor="var(--ant-color-primary)"
        />

        {/* 详细进度 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
            padding: 12,
            backgroundColor: 'var(--ant-color-fill-quaternary)',
            borderRadius: 8,
            fontSize: 12,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--ant-color-text-tertiary)' }}>帖子</div>
            <div style={{ color: 'var(--ant-color-text)' }}>
              {task.processedPosts}/{task.totalPosts}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--ant-color-text-tertiary)' }}>图片</div>
            <div style={{ color: 'var(--ant-color-text)' }}>
              {task.uploadedImages}/{task.totalImages}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--ant-color-text-tertiary)' }}>视频</div>
            <div style={{ color: 'var(--ant-color-text)' }}>
              {task.uploadedVideos}/{task.totalVideos}
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        {!isFinished && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
            {isPaused ? (
              <Button onClick={resumeImport}>继续</Button>
            ) : (
              <Button onClick={pauseImport}>暂停</Button>
            )}
            <Button danger onClick={cancelImport}>
              取消
            </Button>
          </div>
        )}

        {/* 错误信息 */}
        {isFailed && task.errorMessages && (
          <Alert
            type="error"
            message="导入失败"
            description={task.errorMessages.split('\n').slice(0, 3).join('\n')}
            showIcon
          />
        )}

        {/* 完成信息 */}
        {isCompleted && (
          <Alert
            type="success"
            message="导入完成"
            description={`成功导入 ${task.processedPosts} 篇帖子${task.skippedCount > 0 ? `，跳过 ${task.skippedCount} 项` : ''}`}
            showIcon
          />
        )}
      </div>
    );
  };

  // 渲染 Footer
  const renderFooter = () => {
    const isFinished =
      currentTask?.status === 'COMPLETED' ||
      currentTask?.status === 'FAILED' ||
      currentTask?.status === 'CANCELLED';

    if (currentStep === 2) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {isFinished ? (
            <Button type="primary" onClick={handleClose}>
              完成
            </Button>
          ) : (
            <Button onClick={handleClose}>后台运行</Button>
          )}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleClose}>取消</Button>
        <Space>
          {currentStep > 0 && (
            <Button onClick={handlePrev} disabled={previewLoading}>
              上一步
            </Button>
          )}
          <Button
            type="primary"
            onClick={handleNext}
            disabled={!canNext || previewLoading}
            loading={previewLoading}
          >
            {currentStep === 1 ? '开始导入' : '下一步'}
          </Button>
        </Space>
      </div>
    );
  };

  return (
    <Modal
      title={
        <span>
          <CloudUploadOutlined style={{ marginRight: 8 }} />
          导入本地数据到云端
        </span>
      }
      open={visible}
      onCancel={handleClose}
      width={600}
      footer={renderFooter()}
      maskClosable={false}
      destroyOnClose
    >
      {/* 步骤条 */}
      <Steps
        current={currentStep}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          {
            title: '选择数据',
            icon: <FolderOpenOutlined />,
          },
          {
            title: '确认配置',
            icon: <SettingOutlined />,
          },
          {
            title: '执行导入',
            icon: <CloudUploadOutlined />,
          },
        ]}
      />

      {/* 错误提示 */}
      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          showIcon
          closable
        />
      )}

      {/* 步骤内容 */}
      {renderStepContent()}
    </Modal>
  );
};

// 统计项组件
const StatItem: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--ant-color-text)' }}>{value}</div>
    <div style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>{label}</div>
  </div>
);

export default CloudImportModal;
